import * as globals from '@wandb/weave/common/css/globals.styles';

export enum SemanticLegendPalette {
  Sunset,
  Jungle,
  Electricity,
  Sky,
  Clay,
  Rainbow,
}

export const SEMANTIC_LEGEND_PALETTE_DEFAULT: SemanticLegendPalette =
  SemanticLegendPalette.Sunset;
export const SEMANTIC_LEGEND_PALETTE_CATEGORICAL_CONFIG_DEFAULT: SemanticLegendPalette =
  SemanticLegendPalette.Rainbow;

const PALETTES = [
  SemanticLegendPalette.Sunset,
  SemanticLegendPalette.Jungle,
  SemanticLegendPalette.Electricity,
  SemanticLegendPalette.Sky,
  SemanticLegendPalette.Clay,
  SemanticLegendPalette.Rainbow,
];

type PaletteConfig = {
  palette: SemanticLegendPalette;
  getColorsForNumberOfBuckets: (numBuckets: number | undefined) => string[];
};

const SUNSET_PALETTE_CONFIG: PaletteConfig = {
  palette: SemanticLegendPalette.Sunset,
  getColorsForNumberOfBuckets: numBuckets => {
    switch (numBuckets) {
      case 1:
        return ['#FFC960'];
      case 2:
        return ['#FFB25F', '#A22FA8'];
      case 3:
        return ['#FFB75F', '#E76974', '#9B29A5'];
      case 4:
        return ['#FFBD5F', '#F78362', '#D05283', '#9424A2'];
      case 5:
        return ['#FFC360', '#FC945C', '#E66971', '#BF458A', '#8C209F'];
      case 6:
        return [
          '#FFC960',
          '#FEA157',
          '#F27B65',
          '#D75A79',
          '#B13C8E',
          '#851B9C',
        ];
      case 7:
        return [
          '#FFCF61',
          '#FFAD56',
          '#FA885D',
          '#E66A6E',
          '#C94F80',
          '#A6358F',
          '#7D1799',
        ];
      default:
        return [
          '#FFD561',
          '#FFB654',
          '#FE9557',
          '#F17764',
          '#DA5D75',
          '#BD4684',
          '#9B2F90',
          '#751296',
        ];
    }
  },
};

const JUNGLE_PALETTE_CONFIG: PaletteConfig = {
  palette: SemanticLegendPalette.Jungle,
  getColorsForNumberOfBuckets: numBuckets => {
    switch (numBuckets) {
      case 1:
        return ['#69C27D'];
      case 2:
        return ['#FFD561', '#0097AB'];
      case 3:
        return ['#FFD561', '#69C27D', '#048FA2'];
      case 4:
        return ['#FFD561', '#9BC96A', '#34B18B', '#08889A'];
      case 5:
        return ['#FFD561', '#B3CA62', '#66BB79', '#1CA28C', '#098092'];
      case 6:
        return [
          '#FFD561',
          '#C1CB5F',
          '#82C06F',
          '#48AE7F',
          '#10968A',
          '#0A798A',
        ];
      case 7:
        return [
          '#FFD561',
          '#CBCB5D',
          '#96C268',
          '#61B576',
          '#34A281',
          '#0C8B84',
          '#097181',
        ];
      default:
        return [
          '#FFD561',
          '#D0D060',
          '#A2C868',
          '#75BC72',
          '#4BAC7C',
          '#269881',
          '#096A7A',
          '#078280',
        ];
    }
  },
};

const ELECTRICITY_PALETTE_CONFIG: PaletteConfig = {
  palette: SemanticLegendPalette.Electricity,
  getColorsForNumberOfBuckets: numBuckets => {
    switch (numBuckets) {
      case 1:
        return ['#8E3BB2'];
      case 2:
        return ['#5675F0', '#851D4A'];
      case 3:
        return ['#517DF2', '#8E3BB2', '#811B3F'];
      case 4:
        return ['#4C85F4', '#8753D3', '#912C89', '#7D1A34'];
      case 5:
        return ['#468DF6', '#8064E2', '#923FB2', '#8E256D', '#79192A'];
      case 6:
        return [
          '#701911',
          '#7971EB',
          '#904FC9',
          '#943495',
          '#8A2159',
          '#751820',
        ];
      case 7:
        return [
          '#399BF9',
          '#747BF1',
          '#8E5BD6',
          '#9740AA',
          '#932B72',
          '#841E3B',
          '#701911',
        ];
      default:
        return [
          '#32A3FA',
          '#6E86F5',
          '#8A6AE3',
          '#9750C3',
          '#993997',
          '#912967',
          '#821D37',
          '#6B190A',
        ];
    }
  },
};

const SKY_PALETTE_CONFIG: PaletteConfig = {
  palette: SemanticLegendPalette.Sky,
  getColorsForNumberOfBuckets: numBuckets => {
    switch (numBuckets) {
      case 1:
        return ['#2880EF'];
      case 2:
        return ['#77ACF7', '#1E65D9'];
      case 3:
        return ['#7FB1F8', '#3E8DF5', '#1E63D4'];
      case 4:
        return ['#86B6F9', '#599CF7', '#2880EF', '#1D61CF'];
      case 5:
        return ['#8EBBFA', '#6AA6F8', '#4091F6', '#2679E3', '#1E5FC7'];
      case 6:
        return [
          '#95BFFB',
          '#76ADF9',
          '#559AF7',
          '#2B86F3',
          '#236FDA',
          '#1D58BF',
        ];
      case 7:
        return [
          '#9CC4FB',
          '#81B4F9',
          '#65A4F8',
          '#4093F6',
          '#2980E9',
          '#226BD1',
          '#1C55B8',
        ];
      default:
        return [
          '#A4C9FC',
          '#8DBAFA',
          '#77ACF7',
          '#609CF3',
          '#498BEE',
          '#3178E7',
          '#1E65D9',
          '#1951B1',
        ];
    }
  },
};

const CLAY_PALETTE_CONFIG: PaletteConfig = {
  palette: SemanticLegendPalette.Clay,
  getColorsForNumberOfBuckets: numBuckets => {
    switch (numBuckets) {
      case 1:
        return ['#C3552F'];
      case 2:
        return ['#FAA27B', '#B74D28'];
      case 3:
        return ['#FAA580', '#E66C3E', '#AB4928'];
      case 4:
        return ['#FBAA85', '#EF8153', '#D96034', '#A04628'];
      case 5:
        return ['#FCAE8A', '#F39066', '#E46D40', '#C3552F', '#944228'];
      case 6:
        return [
          '#FDB28F',
          '#F59B73',
          '#EA7B50',
          '#D96136',
          '#B25030',
          '#883E28',
        ];
      case 7:
        return [
          '#FEB694',
          '#F89E77',
          '#EF875B',
          '#E16C3F',
          '#C85832',
          '#A3492C',
          '#7C3B27',
        ];
      default:
        return [
          '#FFBA9A',
          '#FAA37D',
          '#F28C61',
          '#E87446',
          '#DA6339',
          '#B75330',
          '#94462C',
          '#703726',
        ];
    }
  },
};

const RAINBOW_PALETTE_CONFIG: PaletteConfig = {
  palette: SemanticLegendPalette.Rainbow,
  getColorsForNumberOfBuckets: numBuckets => {
    switch (numBuckets) {
      case 1:
        return [globals.TEAL_500];
      case 2:
        return [globals.SIENNA_400, globals.TEAL_500];
      case 3:
        return [globals.RED_450, globals.GOLD_400, globals.TEAL_500];
      case 4:
        return [
          globals.RED_450,
          globals.SIENNA_400,
          globals.GOLD_400,
          globals.TEAL_500,
        ];
      case 5:
        return [
          globals.RED_450,
          globals.SIENNA_400,
          globals.GOLD_400,
          globals.CACTUS_450,
          globals.TEAL_500,
        ];
      case 6:
        return [
          globals.RED_450,
          globals.SIENNA_400,
          globals.GOLD_400,
          globals.CACTUS_450,
          globals.GREEN_450,
          globals.TEAL_500,
        ];
      case 7:
        return [
          globals.RED_450,
          globals.SIENNA_400,
          globals.GOLD_400,
          globals.CACTUS_450,
          globals.GREEN_450,
          globals.TEAL_500,
          globals.BLUE_500,
        ];
      default:
        return [
          globals.MAGENTA_500,
          globals.RED_450,
          globals.SIENNA_400,
          globals.GOLD_400,
          globals.CACTUS_450,
          globals.GREEN_450,
          globals.TEAL_500,
          globals.BLUE_500,
        ];
    }
  },
};

function getPaletteConfig(
  semanticLegendPalette: SemanticLegendPalette | undefined
): PaletteConfig {
  const palette = semanticLegendPalette ?? SEMANTIC_LEGEND_PALETTE_DEFAULT;
  switch (palette) {
    case SemanticLegendPalette.Sunset:
      return SUNSET_PALETTE_CONFIG;
    case SemanticLegendPalette.Jungle:
      return JUNGLE_PALETTE_CONFIG;
    case SemanticLegendPalette.Electricity:
      return ELECTRICITY_PALETTE_CONFIG;
    case SemanticLegendPalette.Sky:
      return SKY_PALETTE_CONFIG;
    case SemanticLegendPalette.Clay:
      return CLAY_PALETTE_CONFIG;
    case SemanticLegendPalette.Rainbow:
      return RAINBOW_PALETTE_CONFIG;
  }
}

export function getPaletteForNumberOfBuckets(
  semanticLegendPalette: SemanticLegendPalette,
  numBuckets: number | undefined
): string[] {
  return getPaletteConfig(semanticLegendPalette).getColorsForNumberOfBuckets(
    numBuckets
  );
}

export function getAllPalettesForNumberOfBuckets(
  numBuckets: number | undefined
): {colors: string[]; palette: SemanticLegendPalette}[] {
  return PALETTES.map(palette => ({
    colors: getPaletteForNumberOfBuckets(palette, numBuckets),
    palette,
  }));
}
